import { ApiResponse } from '@store/base';
import {
  AsCareApplyComplete,
  AsCareApplySheet,
  AsCareCancelList,
  AsCareCancelReceipt,
  AsCareList,
  AsCarePayGroupNo,
  AsCarePayRequest,
  AsCareRegNo,
} from '../vo/as-care';

/**
 * 한샘 AS케어+ (연장보증보험) API DTO — order-service `한샘몰 AS케어+`.
 *
 * ⚠️ BE 는 현재 **목업 응답** 단계다(swagger 전체 오퍼레이션에 명시). 스펙은 확정이지만
 * 값은 고정값이 섞여 있어(예: `pay-request` 의 `P_AMT` 는 테스트 결제용 1000원 고정)
 * 금액 대사로 화면을 검증하지 않는다.
 */

/**
 * AS케어+ 업무 응답 코드.
 *
 * swagger 는 이 값들을 HTTP status 자리에 선언하지만, 이 레포의 다른 order-service
 * API 와 마찬가지로 판정은 **응답 본문의 `code`** 로 한다 — HTTP 는 200 으로 오고
 * 업무 실패는 `code` 로 구분한다.
 *
 * ⚠️ 코드 체계가 `7xx` → `9xx` 로 **전면 개편**됐다(order-service swagger 기준).
 * 이전 스펙의 `721`(신청기간 경과)·`722`·`723` 은 더 이상 내려오지 않는다 —
 * 취소 관련 두 건은 `904`·`905` 로 옮겨왔고, `721` 은 대응 코드가 없어졌다.
 *
 * 화면은 이 상수로 **개별 분기**하지 않는다. 「200 이 아니면 서버 `message` 를 얼럿」이
 * 기본이고, 문구는 전부 BE 응답에 실려 온다 — 프론트가 사본을 들고 있으면 두 곳이
 * 갈린다. 여기 이름을 두는 것은 **얼럿이 아닌 처리**가 필요한 코드
 * (`905` 는 전용 다이얼로그, `907` 은 전용 화면, `903` 은 성공 취급)를 가리키기
 * 위해서다.
 */
export const AS_CARE_CODE = {
  /** 정상 */
  OK: 200,
  /** 결제 인증에 실패했습니다. 다시 시도해 주세요 */
  PAY_AUTH_FAILED: 900,
  /** 결제 승인에 실패했습니다. 다시 시도해 주세요 */
  PAY_APPROVE_FAILED: 901,
  /** 결제 금액이 일치하지 않습니다. 고객센터로 문의해 주세요 (1588-0900) */
  PAY_AMOUNT_MISMATCH: 902,
  /** 이미 처리된 결제입니다 — 재전송·중복 확정으로 들어온다(BE 멱등 응답) */
  PAY_ALREADY_DONE: 903,
  /** 보험종료일에는 보험취소가 불가능합니다 (이전 `722`) */
  CANCEL_ON_END_DATE: 904,
  /** 취소가 불가능합니다 고객센터로 문의해 주세요 (1588-0900) (이전 `723`) */
  CANCEL_UNAVAILABLE: 905,
  /** 이미 처리 중인 취소 요청입니다. 잠시 후 다시 확인해 주세요 */
  CANCEL_IN_PROGRESS: 906,
  /**
   * 주문 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
   *
   * 목록 화면(SC-02 · SC-08)에서는 **얼럿이 아니라 전용 화면**으로 낸다 — 목록 자체가
   * 비어 있는 상태라 얼럿을 닫으면 「대상 없음」과 구분되지 않는다.
   */
  ORDER_LOOKUP_FAILED: 907,
  /** 주문 정보가 변경되어 신청할 수 없습니다. 목록에서 다시 확인해 주세요 */
  ORDER_CHANGED: 908,
} as const;

export type AsCareCode = (typeof AS_CARE_CODE)[keyof typeof AS_CARE_CODE];

/**
 * 보험신청/내역 조회 (SC-02).
 * 파라미터가 없다 — 대상 회원은 토큰으로 특정한다.
 */
export interface GetAsCareListResponse extends ApiResponse<AsCareList> {}

/** 보험신청서 조회 (SC-03) */
export interface GetAsCareApplySheetRequest {
  ordNo: string;
  /** 신청 대상 주문순번 목록 */
  ordSeqList: number[];
}

export interface GetAsCareApplySheetResponse
  extends ApiResponse<AsCareApplySheet> {}

/**
 * 보험료 결제요청 파라미터 조회 (SC-04 Step 2).
 * BE 가 신청서 조회와 같은 요청 DTO 를 쓴다 — 선택 상품이 바뀌면 총 보험료가 바뀌므로
 * 결제 직전에 같은 입력으로 서버가 금액을 다시 확정한다.
 */
export interface GetAsCarePayRequestRequest {
  ordNo: string;
  ordSeqList: number[];
}

export interface GetAsCarePayRequestResponse
  extends ApiResponse<AsCarePayRequest> {}

/**
 * 보험신청 확정 (SC-05) — PG 인증결과 중계.
 *
 * 필드명이 `P_*` 인 것은 이니시스 인증결과를 **그대로** 넘기기 때문이다.
 *
 * ⚠️ 승인 주소를 **서버가 만든다.** 프론트는 `P_IDCNAME`(센터명)만 넘기고, BE 가
 * 허용 목록과 대조해 승인 URL 을 조립한다. 이전 스펙은 프론트가 해석한
 * `P_REQ_URL` 을 받았는데, 승인 주소를 결정하는 로직이 클라이언트 경로에 있는
 * 구조였다.
 *
 * `P_AMT` 도 신뢰하지 않고 서버 산출 총 보험료와 대조한다.
 */
export interface CompleteAsCareApplyRequest {
  /** 인증결과 코드. "00" 이 성공 */
  P_STATUS: string;
  /** 인증결과 메시지 (실패 사유 기록용) */
  P_RMESG?: string;
  /** 가맹점 아이디 */
  P_MID: string;
  /** 인증거래번호 — 승인 요청의 키 */
  P_AUTH_TID: string;
  /** 승인요청을 보낼 IDC 센터 (BE 가 허용 목록과 대조해 URL 을 만든다) */
  P_IDCNAME: string;
  /** 가맹점 주문번호 = 보험결제그룹번호 */
  P_OID: AsCarePayGroupNo;
  /** 인증된 결제금액 */
  P_AMT: string;
}

export interface CompleteAsCareApplyResponse
  extends ApiResponse<AsCareApplyComplete> {}

/** 보험취소 접수 (SC-07) */
export interface CancelAsCareRequest {
  /** 취소 대상 신청 회차 */
  ewPayGrpNo: AsCarePayGroupNo;
  /** 취소 대상 보험가입번호 목록 */
  ewRegNoList: AsCareRegNo[];
}

export interface CancelAsCareResponse
  extends ApiResponse<AsCareCancelReceipt> {}

/**
 * 보험취소/내역 조회 (SC-08).
 * 파라미터가 없다. 환급이 완료된 건만 내려온다.
 */
export interface GetAsCareCancelListResponse
  extends ApiResponse<AsCareCancelList> {}
